import { sharedSheet } from "../styles/sharedStyles.js";
import componentStylesText from "./SimpleTextBlockComponent.css?inline";

const componentSheet = new CSSStyleSheet();
componentSheet.replaceSync(componentStylesText);

/**
 * SimpleTextBlockComponent — A leaf component for rendering a single text bubble in the chat.
 *
 * It renders the following structure:
 * <div class="chat-message__bubble">
 *   <span class="chat-message__text"></span>
 * </div>
 *
 * Attributes:
 * - text: The text content to display.
 */
export class SimpleTextBlockComponent extends HTMLElement {
    private textSpan: HTMLSpanElement | null = null;

    /**
     * Specifies which attributes should be monitored for changes.
     */
    static get observedAttributes(): string[] {
        return ["text", "role"];
    }

    constructor() {
        super();
    }

    /**
     * Returns the primary text content for accessibility aggregation.
     */
    get accessibilityText(): string {
        return this.getAttribute("text") || "";
    }

    /**
     * Called when the element is first connected to the document's DOM.
     * Initializes the internal structure if not already present.
     */
    connectedCallback(): void {
        if (!this.shadowRoot) {
            const shadow = this.attachShadow({ mode: "open" });
            shadow.adoptedStyleSheets = [sharedSheet, componentSheet];
            this.render();
        }
        this.updateText();
        this.dispatchEvent(new CustomEvent("text-update", { bubbles: true }));
    }

    /**
     * Called when one of the observed attributes changes.
     * @param name The name of the attribute that changed.
     * @param _oldValue The previous value of the attribute (unused).
     * @param _newValue The new value of the attribute (unused).
     */
    attributeChangedCallback(name: string, _oldValue: string | null, _newValue: string | null): void {
        if (name === "text") {
            this.updateText();
            this.dispatchEvent(new CustomEvent("text-update", { bubbles: true }));
        }
    }

    /**
     * Creates the internal DOM structure of the component.
     * Uses standard CSS classes for styling compatibility.
     */
    private render(): void {
        const bubble = document.createElement("div");
        bubble.className = "chat-message__bubble";

        this.textSpan = document.createElement("span");
        this.textSpan.className = "chat-message__text";

        bubble.appendChild(this.textSpan);
        this.shadowRoot?.appendChild(bubble);
    }

    /**
     * Updates the text content of the internal span.
     * Uses textContent to prevent XSS.
     */
    private updateText(): void {
        if (this.textSpan) {
            this.textSpan.textContent = this.getAttribute("text") || "";
        }
    }
}

/**
 * Register the custom element if it hasn't been registered yet.
 */
if (!customElements.get("jt-simple-text-block")) {
    customElements.define("jt-simple-text-block", SimpleTextBlockComponent);
}
